import React, { useEffect, useState } from "react";
import { Box, Text, VStack, HStack, Avatar, Spinner } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { api } from "../services/api";

interface Contributor {
  user: string;
  contributionCount: number;
  lastContribution: string;
}

// Pulsing glow animation
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 10px rgba(0, 162, 255, 0.3); }
  50% { box-shadow: 0 0 20px rgba(0, 162, 255, 0.5); }
`;

const TopContributors: React.FC = () => {
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopContributors = async () => {
      try {
        const res = await api.get("/api/discussion/top-contributors");
        const data = res.data || [];

        // If no data from API, use fallback sample data
        if (data.length === 0) {
          setContributors([
            { user: "FactChecker", contributionCount: 42, lastContribution: new Date().toISOString() },
            { user: "TruthSeeker", contributionCount: 27, lastContribution: new Date().toISOString() },
            { user: "LogicLover", contributionCount: 15, lastContribution: new Date().toISOString() },
          ]);
        } else {
          setContributors(data);
        }
      } catch (error) {
        console.error("Failed to fetch top contributors:", error);
        // On error, show fallback data
        setContributors([
          { user: "FactChecker", contributionCount: 42, lastContribution: new Date().toISOString() },
          { user: "TruthSeeker", contributionCount: 27, lastContribution: new Date().toISOString() },
          { user: "LogicLover", contributionCount: 15, lastContribution: new Date().toISOString() },
        ]);
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchTopContributors();

    // Listen for new activity to refresh stats
    const handleActivityUpdate = () => {
      console.log('🔄 [TopContributors] Activity detected, refreshing...');
      fetchTopContributors();
    };

    window.addEventListener('discussion-posted', handleActivityUpdate);
    window.addEventListener('activity-updated', handleActivityUpdate);

    return () => {
      window.removeEventListener('discussion-posted', handleActivityUpdate);
      window.removeEventListener('activity-updated', handleActivityUpdate);
    };
  }, []);

  if (loading) {
    return (
      <Box
        p={3}
        background="rgba(0, 0, 0, 0.4)"
        border="1px solid rgba(0, 162, 255, 0.3)"
        borderRadius="8px"
        textAlign="center" _light={{ background: "rgba(120, 96, 64, 0.1)", border: "1px solid rgba(38, 53, 85, 0.4)" }}
      >
        <Spinner size="sm" color="#00a2ff" _light={{ color: "#263555" }} />
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      w="full"
      background="rgba(0, 0, 0, 0.4)"
      border="1px solid rgba(0, 162, 255, 0.3)"
      borderRadius="8px"
      p={{ base: 2, xl: 3 }}
      overflow="hidden" _light={{ background: "rgba(120, 96, 64, 0.1)", border: "1px solid rgba(38, 53, 85, 0.4)" }}
    >
      {/* Scanlines */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        background="repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 162, 255, 0.03) 2px, rgba(0, 162, 255, 0.03) 4px)"
        pointerEvents="none"
        borderRadius="8px"
        zIndex={1} _light={{ background: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 162, 255, 0.013) 2px, rgba(0, 162, 255, 0.013) 4px)" }}
      />

      <Box position="relative" zIndex={2}>
        {/* Header */}
        <Text
          color="#00a2ff"
          fontSize={{ base: "0.55rem", xl: "0.65rem" }}
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="1px"
          mb={2}
          textAlign="center"
          textShadow="0 0 8px rgba(0, 162, 255, 0.6)" _light={{ color: "#263555", textShadow: "none" }}
        >
          ◈ Active Contributors ◈
        </Text>

        {/* Contributors List */}
        <VStack spacing={2} align="stretch">
          {contributors.map((contributor, index) => (
              <Box
                key={contributor.user}
                p={{ base: 1.5, xl: 2 }}
                background={
                  index === 0
                    ? "rgba(0, 162, 255, 0.1)"
                    : "rgba(15, 23, 42, 0.5)"
                }
                border={
                  index === 0
                    ? "1px solid rgba(0, 162, 255, 0.4)"
                    : "1px solid rgba(100, 116, 139, 0.2)"
                }
                borderRadius="6px"
                transition="all 0.3s ease"
                _hover={{
                  background: "rgba(0, 162, 255, 0.15)",
                  borderColor: "rgba(0, 162, 255, 0.5)",
                  transform: "translateX(2px)", _light: { background: "rgba(0, 162, 255, 0.068)", borderColor: "rgba(38, 53, 85, 0.4)" }
                }}
                sx={
                  index === 0
                    ? {
                        animation: `${pulseGlow} 2s ease-in-out infinite`,
                      }
                    : {}
                } _light={{ background: (index === 0) ? "rgba(0, 162, 255, 0.045)" : "rgba(120, 96, 64, 0.1)", border: (index === 0) ? "1px solid rgba(38, 53, 85, 0.4)" : "1px solid rgba(28, 24, 19, 0.2)" }}
              >
                <HStack spacing={{ base: 1, xl: 2 }}>
                  {/* Rank Badge */}
                  <Box
                    minW={{ base: "16px", xl: "20px" }}
                    h={{ base: "16px", xl: "20px" }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    background={
                      index === 0
                        ? "linear-gradient(135deg, rgba(250, 204, 21, 0.3), rgba(250, 204, 21, 0.1))"
                        : index === 1
                        ? "linear-gradient(135deg, rgba(192, 192, 192, 0.3), rgba(192, 192, 192, 0.1))"
                        : "linear-gradient(135deg, rgba(205, 127, 50, 0.3), rgba(205, 127, 50, 0.1))"
                    }
                    borderRadius="3px"
                    fontSize={{ base: "0.5rem", xl: "0.6rem" }}
                    fontWeight="700"
                    color={
                      index === 0
                        ? "#fbbf24"
                        : index === 1
                        ? "#d1d5db"
                        : "#cd7f32"
                    }
                  >
                    {index + 1}
                  </Box>

                  {/* Avatar */}
                  <Avatar
                    name={contributor.user}
                    size="xs"
                    bg="rgba(0, 162, 255, 0.3)"
                    color="#00a2ff"
                    w={{ base: "20px", xl: "24px" }}
                    h={{ base: "20px", xl: "24px" }} _light={{ bg: "rgba(0, 162, 255, 0.12)", color: "#263555" }}
                  />

                  {/* User Info */}
                  <VStack spacing={0} align="start" flex={1} minW={0}>
                    <Text
                      fontSize={{ base: "0.6rem", xl: "0.7rem" }}
                      fontWeight="600"
                      color="#e2e8f0"
                      isTruncated
                      w="100%" _light={{ color: "#263555" }}
                    >
                      {contributor.user}
                    </Text>
                    <Text
                      fontSize={{ base: "0.5rem", xl: "0.6rem" }}
                      color="#64748b"
                      fontWeight="500"
                      isTruncated
                      w="100%" _light={{ color: "#5c5347" }}
                    >
                      {contributor.contributionCount} post{contributor.contributionCount !== 1 ? 's' : ''}
                    </Text>
                  </VStack>

                  {/* Activity Indicator */}
                  <Box
                    width={{ base: "5px", xl: "6px" }}
                    height={{ base: "5px", xl: "6px" }}
                    borderRadius="50%"
                    background={
                      index === 0
                        ? "linear-gradient(135deg, #4ade80, #22c55e)"
                        : "#64748b"
                    }
                    boxShadow={
                      index === 0
                        ? "0 0 8px rgba(34, 197, 94, 0.6)"
                        : "none"
                    }
                    flexShrink={0} _light={{ background: (index === 0) ? "linear-gradient(135deg, #e6f3dd, #22c55e)" : "#64748b", boxShadow: (index === 0) ? "var(--paper-lift)" : "none" }}
                  />
                </HStack>
              </Box>
            ))}
        </VStack>
      </Box>
    </Box>
  );
};

export default TopContributors;
